import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, FileText, Loader2, ShieldCheck } from 'lucide-react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { safeFetch } from '../lib/api';
import { auth } from '../lib/firebase';
import OrgPortalSpecialRequests from './shared/OrgPortalSpecialRequests';

/**
 * OrgRestrictionGate (Org Mgmt S4, spec §7) — the client half of the
 * workspace access restriction. The SERVER enforces the restriction at
 * the API layer (requireAuth returns 403 ORG_WORKSPACE_RESTRICTED);
 * this gate renders the matching screens from GET /api/org-access/status:
 *
 *  - A restricted member who is NOT the organization's designated main
 *    user sees the standard restriction message (exact text) heading
 *    the org portal's Special Requests surface in restricted mode —
 *    only the outstanding BLOCKING requests, completable in place
 *    (program 2, T4). The plain message-only screen is the fallback
 *    when no restricted organization context resolves.
 *  - The designated main user sees the limited outstanding-requests
 *    view: what is outstanding, why it is required, and a View Request
 *    action into the normal token completion flow.
 *  - Everyone else (including applicants and staff) sees nothing.
 *
 * Access restores automatically: the gate re-checks periodically and
 * unmounts itself as soon as no mandatory request remains outstanding.
 */

// Kept byte-identical to server/orgRestrictionCore.ts
// ORG_RESTRICTION_MESSAGE — a wiring test pins the equality.
export const ORG_RESTRICTION_MESSAGE =
  'There is something wrong with this workspace. Please contact the main admin.';

const PUBLIC_PATH_PREFIXES = [
  '/special-request/',
  '/accept-invitation',
  '/get-started',
  '/pay/',
  '/quote/',
  '/schedule/',
];

interface OutstandingItem {
  requestId: string;
  title: string;
  requestType: string;
  description: string;
  status: string;
  issuedAt: string | null;
  dueDate: string | null;
  token: string;
}

interface OrgState {
  orgType: string;
  orgId: string;
  orgName: string;
  isMainUser: boolean;
  restricted: boolean;
  outstanding: OutstandingItem[];
}

interface StatusResponse {
  restricted: boolean;
  message: string | null;
  orgs: OrgState[];
}

export const OrgRestrictionGate: React.FC = () => {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [signedIn, setSignedIn] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await safeFetch('/api/org-access/status');
      setStatus(data as StatusResponse);
    } catch {
      // Status unreachable — the API layer still enforces the
      // restriction; never trap the user behind a broken gate.
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' &&
        PUBLIC_PATH_PREFIXES.some((p) => window.location.pathname.startsWith(p))) {
      return;
    }
    const unsub = onAuthStateChanged(auth, (user) => {
      setSignedIn(!!user);
      if (user) load();
      else setStatus(null);
    });
    const interval = setInterval(() => {
      if (auth.currentUser) load();
    }, 60_000);
    return () => {
      unsub();
      clearInterval(interval);
    };
  }, [load]);

  if (!signedIn || !status) return null;

  const mainUserOrgs = (status.orgs || []).filter((o) => o.restricted && o.isMainUser);

  if (!status.restricted && mainUserOrgs.length === 0) return null;

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch { /* session is already gone */ }
    window.location.assign('/');
  };

  // Restricted member (not the designated main user). The exact
  // restriction message heads the surface; below it, the org portal's
  // Special Requests area in restricted mode shows ONLY the
  // outstanding BLOCKING requests and lets the member complete,
  // submit, or correct them in place (program 2, T4 — the org
  // portals are role-state renders without URL routes, so this gate
  // IS the redirected surface; the API contract is unchanged). When
  // no restricted organization context resolves, the plain
  // message-only screen remains the fallback.
  if (status.restricted) {
    const hasRestrictedOrg = (status.orgs || []).some((o) => o.restricted);
    if (!hasRestrictedOrg) {
      return (
        <div className="fixed inset-0 z-[200] min-h-dvh flex flex-col items-center justify-center bg-slate-100 p-6 font-sans text-slate-800">
          <div className="bg-white border border-slate-200 shadow-xl rounded-3xl p-8 max-w-md w-full text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
              <AlertTriangle className="h-8 w-8" />
            </div>
            <p className="text-sm text-slate-700 leading-relaxed font-medium">
              {ORG_RESTRICTION_MESSAGE}
            </p>
            <button
              onClick={handleSignOut}
              className="py-2.5 px-5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-semibold transition cursor-pointer"
            >
              Sign Out
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="fixed inset-0 z-[200] min-h-dvh overflow-y-auto bg-slate-100 p-4 sm:p-6 font-sans text-slate-800">
        <div className="max-w-3xl mx-auto space-y-5 py-6">
          <div className="bg-white border border-slate-200 shadow-xl rounded-3xl p-6 text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
              <AlertTriangle className="h-7 w-7" />
            </div>
            <p className="text-sm text-slate-700 leading-relaxed font-medium">
              {ORG_RESTRICTION_MESSAGE}
            </p>
            <p className="text-xs text-slate-500 leading-relaxed max-w-md mx-auto">
              Access is paused until the outstanding requests below are resolved.
              You can complete them right here — access restores automatically
              once nothing blocking remains outstanding.
            </p>
          </div>
          <div className="bg-white border border-slate-200 shadow-sm rounded-3xl p-4 sm:p-6 text-slate-800">
            <OrgPortalSpecialRequests restrictedOnly heading="Requests requiring attention" />
          </div>
          <div className="text-center">
            <button
              onClick={handleSignOut}
              className="py-2.5 px-5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-semibold transition cursor-pointer"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Designated main user: the limited outstanding-requests view.
  return (
    <div className="fixed inset-0 z-[200] min-h-dvh overflow-y-auto bg-slate-100 p-6 font-sans text-slate-800">
      <div className="max-w-2xl mx-auto space-y-6 py-8">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center mx-auto">
            <ShieldCheck className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-bold text-slate-900">Action Required</h2>
          <p className="text-xs text-slate-500 leading-relaxed max-w-md mx-auto">
            Your workspace is paused until the outstanding mandatory request
            {mainUserOrgs.reduce((n, o) => n + o.outstanding.length, 0) > 1 ? 's below are' : ' below is'} resolved.
            Complete the request to restore full access for everyone in the organization.
          </p>
        </div>
        {mainUserOrgs.map((org) => (
          <div key={org.orgId} className="bg-white border border-slate-200 shadow-sm rounded-3xl p-6 space-y-4">
            <h3 className="text-sm font-bold text-slate-900">{org.orgName}</h3>
            {org.outstanding.map((item) => (
              <div key={item.requestId} className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-violet-600 shrink-0" />
                    {item.title}
                  </p>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                    {item.requestType} · {item.status}
                  </span>
                </div>
                {item.description && (
                  <p className="text-xs text-slate-600 leading-relaxed">{item.description}</p>
                )}
                <p className="text-[11px] text-slate-500">
                  {item.issuedAt ? `Issued ${new Date(item.issuedAt).toLocaleDateString()} · ` : ''}
                  {item.dueDate ? `Due ${new Date(item.dueDate).toLocaleDateString()}` : 'No due date'}
                </p>
                <button
                  onClick={() => window.location.assign(`/special-request/${item.token}`)}
                  className="py-2 px-4 btn-primary text-white rounded-xl text-xs font-bold cursor-pointer"
                >
                  View Request
                </button>
              </div>
            ))}
          </div>
        ))}
        <div className="text-center">
          <button
            onClick={handleSignOut}
            className="py-2.5 px-5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-semibold transition cursor-pointer"
          >
            Sign Out
          </button>
        </div>
        <p className="text-center text-[11px] text-slate-500 flex items-center justify-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" />
          This page refreshes automatically once the request is resolved.
        </p>
      </div>
    </div>
  );
};

export default OrgRestrictionGate;
